<template>
  <Dialog :visible="manageModelDeploymentDialogVisible"
          @update:visible="handleClose">
    <template #header>
      {{ manageModelDeploymentId ? 'Edit' : 'Add' }} AI Model Deployment
    </template>
    <ScrollPanel class="scroll-panel" style="max-height: 60vh">
      <div class="form-element">
        Name:
        <InputText v-model="manageModelDeploymentName" :invalid="manageModelDeploymentNameInvalid" class="form-element full-width" type="text"
                   @change="manageModelDeploymentNameChange"/>
        <div v-if="manageModelDeploymentNameInvalid">
          <InlineMessage severity="error">Model Deployment Name is required.</InlineMessage>
        </div>
      </div>

      <div class="form-element">
        Machine Name:
        <InputText v-model="manageModelDeploymentMachineName" :invalid="manageModelDeploymentMachineNameEmpty || manageModelDeploymentMachineNameInvalid" class="form-element full-width" type="text"
                   @change="manageModelDeploymentMachineNameChange"/>
        <div v-if="manageModelDeploymentMachineNameEmpty">
          <InlineMessage severity="error">Model Deployment Name is required.</InlineMessage>
        </div>
        <div v-if="manageModelDeploymentMachineNameInvalid">
          <InlineMessage severity="error">Model Deployment Name may contain only uppercase letters and underscores.</InlineMessage>
        </div>
      </div>

      <div class="form-element">
        Model:
        <Dropdown :options="models" option-label="name" option-value="id"
                  v-model="manageModelDeploymentModel"
                  :invalid="manageModelDeploymentModelInvalid"
                  class="ml-half"
                  @change="manageModelDeploymentModelChange"
        ></Dropdown>
        <div class="mt-half" v-if="manageModelDeploymentModelInvalid">
          <InlineMessage severity="error">Model is required.</InlineMessage>
        </div>
      </div>

      <div class="form-element">
        Status:
        <Dropdown :options="statusOptions" option-label="name" option-value="id"
                  v-model="manageModelDeploymentStatus"
                  :invalid="manageModelDeploymentStatusInvalid"
                  class="ml-half"
                  @change="manageModelDeploymentStatusChange"
        ></Dropdown>
        <div class="mt-half" v-if="manageModelDeploymentStatusInvalid">
          <InlineMessage severity="error">Status is required.</InlineMessage>
        </div>
      </div>

      <div class="form-element">
        Deployment Date:
        <InputText v-model="manageModelDeploymentDeploymentDate" :invalid="manageModelDeploymentDeploymentDateInvalid" class="form-element full-width" type="text"
                   @change="manageModelDeploymentDeploymentDateChange"/>
        <div v-if="manageModelDeploymentDeploymentDateInvalid">
          <InlineMessage severity="error">Deployment Date must be in the format mm/dd/yyyy.</InlineMessage>
        </div>
      </div>

      <div class="form-element">
        Discontinued Date:
        <InputText v-model="manageModelDeploymentDiscontinuedDate" :invalid="manageModelDeploymentDiscontinuedDateInvalid" class="form-element full-width" type="text"
                   @change="manageModelDeploymentDiscontinuedDateChange"/>
        <div v-if="manageModelDeploymentDiscontinuedDateInvalid">
          <InlineMessage severity="error">Discontinued Date must be in the format mm/dd/yyyy.</InlineMessage>
        </div>
      </div>

      <div class="form-element">
        API URL:
        <InputText v-model="manageModelDeploymentApiUrl" :invalid="manageModelDeploymentApiUrlInvalid" class="form-element full-width" type="text"
                   @change="manageModelDeploymentApiUrlChange"/>
        <div v-if="manageModelDeploymentApiUrlInvalid">
          <InlineMessage severity="error">API URL is required.</InlineMessage>
        </div>
        <div class="text-sm">
          <div class="mb-half">The domain name must be one of our approved domains. If your domain is not supported, please contact support to ensure that your deployment will work correctly. Currently we support:</div>
          <div>*.ai.azure.com</div>
          <div>*.openai.azure.com</div>
          <div>api.cohere.ai</div>
          <div>api.anthropic.com</div>
          <div>api.openai.com</div>
        </div>
      </div>

      <div class="form-element">
        Credential:
        <Dropdown :options="modelDeploymentCredentials" option-label="name" option-value="id"
                  v-model="manageModelDeploymentCredential"
                  :invalid="manageModelDeploymentCredentialInvalid"
                  class="ml-half"
                  @change="manageModelDeploymentCredentialChange"
        ></Dropdown>
        <div class="mt-half" v-if="manageModelDeploymentCredentialInvalid">
          <InlineMessage severity="error">Credential is required.</InlineMessage>
        </div>
      </div>
    </ScrollPanel>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Cancel" severity="secondary" type="button" @click="handleClose"></Button>
        <Button :disabled="manageModelDeploymentSubmitting" :loading="manageModelDeploymentSubmitting" label="Save" type="button" @click="saveModelDeployment"></Button>
      </div>
    </template>
  </Dialog>

  <div v-if="hasAdminAccess">
    <ProgressSpinnerContainer v-if="modelDeploymentsLoading" />
    <div v-else class="card">
      <div class="header-container mb flex">
        <h2 class="flex-1">AI Model Deployments</h2>
        <div class="align-right mr">
          <Button @click="manageModelDeployment(null)"><i class="pi pi-plus"></i> Add AI Model Deployment</Button>
        </div>
      </div>
      <ScrollPanel class="scroll-panel" style="max-height: 60vh">
        <DataTable :value="computedModelDeployments">
          <Column field="name" header="Name"></Column>
          <Column field="modelProvider" header="Model Provider"></Column>
          <Column field="status" header="Status">
            <template #body="slotProps">
              {{ formatStatus(slotProps.data.status) }}
            </template>
          </Column>
          <Column field="deploymentDate" header="Deployment Date"></Column>
          <Column field="discontinuedDate" header="Discontinued Date"></Column>
          <Column field="sphereName" header="Sphere">
            <template #body="slotProps" class="flex align-items-center">
              {{ getSphereById(slotProps.data.platformAccountId) }}
            </template>
          </Column>
          <Column header="Actions">
            <template #body="slotProps">
              <Button v-if="slotProps.data.platformAccountId > 0" class="ml" severity="info" @click="manageModelDeployment(slotProps.data)" label="Edit"></Button>
              <Button v-if="slotProps.data.platformAccountId > 0" class="ml" severity="danger" @click="deleteModelDeployment(slotProps.data)" label="Delete"></Button>
            </template>
          </Column>
        </DataTable>
      </ScrollPanel>
    </div>
  </div>
  <div v-else>
    You do not have permission to access this page.
  </div>
</template>

<script setup>
import { useConfirm } from "primevue/useconfirm";
import ProgressSpinnerContainer from "../components/ProgressSpinnerContainer.vue";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";
import { useAccounts, useModelDeployments, useUsers } from "../composables/entities.js";
import { selectedAccount } from "../store/state.js";
import { deleteRequest, patch, post } from "../util/api.js";
import { decodeModelDeployment, isValidDate } from "../util/util.js";

const { models, modelDeployments, modelDeploymentCredentials, modelDeploymentsLoading } = useModelDeployments()

const { accounts } = useAccounts()

const { hasAdminAccess } = useUsers()

const toast = useToast()

const confirm = useConfirm()

const computedModelDeployments = computed(() => modelDeployments.value.slice().sort((a, b) => (a.status === b.status ? a.name?.localeCompare(b.name) : a.status?.localeCompare(b.status))))

function getSphereById(accountId) {
  if (String(accountId) === '0') {
    return 'Global'
  }
  for (let i = 0; i < accounts.value.length; i++) {
    if (String(accounts.value[i].id) === String(accountId)) {
      return accounts.value[i].name
    }
  }
  return ''
}

const statusOptions = [
  { name: 'Pending', id: 'PENDING' },
  { name: 'Active', id: 'ACTIVE' },
  { name: 'Inactive', id: 'INACTIVE' },
  { name: 'Deprecated', id: 'DEPRECATED' },
]

function formatStatus(status) {
  const option = statusOptions.find(o => o.id === status)
  if (option?.name) {
    return option.name.charAt(0).toUpperCase() + option.name.substring(1).toLowerCase()
  }
  return ''
}

async function deleteModelDeployment(data) {
  confirm.require({
    message: 'Are you sure you wish to delete ' + data.name + '?',
    header: 'Confirm Delete AI Model Deployment',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        modelDeploymentsLoading.value = true
        const request = await deleteRequest('/crbn-tech/llm-model-deployments/' + data.id)
        if (request.id) {
          modelDeployments.value = modelDeployments.value.filter(md => String(md.id) !== String(request.id))
        }
        else {
          handleError()
        }
      }
      catch (e) {
        handleError(e)
      }
      finally {
        modelDeploymentsLoading.value = false
      }
      return true
    },
    reject: () => {
      return true
    }
  })
}

watch(modelDeploymentsLoading, (newValue)=> {
  if (!newValue) {
    if (!hasAdminAccess.value) {
      //router.replace('/')
    }
  }
}, { immediate: true })

const manageModelDeploymentDialogVisible = ref(false)
const manageModelDeploymentSubmitting = ref(false)

const manageModelDeploymentId = ref(null)
const manageModelDeploymentName = ref(null)
const manageModelDeploymentMachineName = ref(null)
const manageModelDeploymentModel = ref(null)
const manageModelDeploymentStatus = ref(null)
const manageModelDeploymentDeploymentDate = ref(null)
const manageModelDeploymentDiscontinuedDate = ref(null)
const manageModelDeploymentMetadata = ref(null)
const manageModelDeploymentApiUrl = ref(null)
const manageModelDeploymentCredential = ref(null)

const manageModelDeploymentNameInvalid = ref(false)
const manageModelDeploymentMachineNameEmpty = ref(false)
const manageModelDeploymentMachineNameInvalid = ref(false)
const manageModelDeploymentApiUrlInvalid = ref(false)
const manageModelDeploymentStatusInvalid = ref(false)
const manageModelDeploymentDeploymentDateInvalid = ref(false)
const manageModelDeploymentDiscontinuedDateInvalid = ref(false)
const manageModelDeploymentModelInvalid = ref(false)
const manageModelDeploymentCredentialInvalid = ref(false)

function manageModelDeploymentNameChange() {
  manageModelDeploymentNameInvalid.value = !manageModelDeploymentName.value
}
function manageModelDeploymentMachineNameChange() {
  manageModelDeploymentMachineNameEmpty.value = !manageModelDeploymentMachineName.value
  manageModelDeploymentMachineNameInvalid.value = !/^[A-Z0-9_]+$/.test(manageModelDeploymentMachineName.value)
}
function manageModelDeploymentApiUrlChange() {
  manageModelDeploymentApiUrlInvalid.value = !manageModelDeploymentApiUrl.value
}
function manageModelDeploymentStatusChange() {
  manageModelDeploymentStatusInvalid.value = false
}

function manageModelDeploymentDeploymentDateChange() {
  manageModelDeploymentDeploymentDateInvalid.value = !!manageModelDeploymentDeploymentDate.value && !isValidDate(manageModelDeploymentDeploymentDate.value)
}
function manageModelDeploymentDiscontinuedDateChange() {
  manageModelDeploymentDiscontinuedDateInvalid.value = !!manageModelDeploymentDiscontinuedDate.value && !isValidDate(manageModelDeploymentDiscontinuedDate.value)
}
function manageModelDeploymentModelChange() {
  manageModelDeploymentModelInvalid.value = false
}
function manageModelDeploymentCredentialChange() {
  manageModelDeploymentCredentialInvalid.value = false
}

function handleClose() {
  manageModelDeploymentId.value = null
  manageModelDeploymentName.value = null
  manageModelDeploymentMachineName.value = null
  manageModelDeploymentModel.value = null
  manageModelDeploymentStatus.value = null
  manageModelDeploymentDeploymentDate.value = null
  manageModelDeploymentDiscontinuedDate.value = null
  manageModelDeploymentMetadata.value = null
  manageModelDeploymentApiUrl.value = null
  manageModelDeploymentCredential.value = null

  manageModelDeploymentNameInvalid.value = false
  manageModelDeploymentMachineNameEmpty.value = false
  manageModelDeploymentMachineNameInvalid.value = false
  manageModelDeploymentApiUrlInvalid.value = false
  manageModelDeploymentStatusInvalid.value = false
  manageModelDeploymentDeploymentDateInvalid.value = false
  manageModelDeploymentDiscontinuedDateInvalid.value = false
  manageModelDeploymentModelInvalid.value = false
  manageModelDeploymentCredentialInvalid.value = false

  manageModelDeploymentDialogVisible.value = false
  manageModelDeploymentSubmitting.value = false
}

function manageModelDeployment(data) {
  manageModelDeploymentDialogVisible.value = true
  manageModelDeploymentId.value = data?.id
  manageModelDeploymentName.value = data?.name
  manageModelDeploymentMachineName.value = data?.machineName
  manageModelDeploymentModel.value = data?.modelId
  manageModelDeploymentStatus.value = data?.status
  manageModelDeploymentDeploymentDate.value = data?.deploymentDate
  manageModelDeploymentDiscontinuedDate.value = data?.discontinuedDate
  manageModelDeploymentMetadata.value = data?.metadata
  manageModelDeploymentApiUrl.value = data?.metadata?.restApi
  manageModelDeploymentCredential.value = data?.credentialsId
}

function handleError(e) {
  manageModelDeploymentSubmitting.value = false
  toast.add({
    severity: 'error',
    group: 'mainToast',
    life: 5000,
    detail: 'An error has occurred.  Please try again.'
  })
}

function resetValidations() {
  manageModelDeploymentNameInvalid.value = false
  manageModelDeploymentMachineNameEmpty.value = false
  manageModelDeploymentMachineNameInvalid.value = false
  manageModelDeploymentStatusInvalid.value = false
  manageModelDeploymentApiUrlInvalid.value = false
  manageModelDeploymentDeploymentDateInvalid.value = false
  manageModelDeploymentDiscontinuedDateInvalid.value = false
  manageModelDeploymentModelInvalid.value = false
}

function convertDateFormat(d) {
  if (!d) {
    return null
  }
  const s = d.split('/')
  return s[2] + '-' + s[0].padStart(2, '0') + '-' + s[1].padStart(2, '0') + 'T00:00:00Z'
}

async function saveModelDeployment() {
  let valid = true
  resetValidations()
  if (!manageModelDeploymentName.value) {
    manageModelDeploymentNameInvalid.value = true
    valid = false
  }
  if (!manageModelDeploymentMachineName.value) {
    manageModelDeploymentMachineNameEmpty.value = true
    valid = false
  }
  else if (!/^[A-Z0-9_]+$/.test(manageModelDeploymentMachineName.value)) {
    manageModelDeploymentMachineNameInvalid.value = true
    valid = false
  }
  if (!manageModelDeploymentApiUrl.value) {
    manageModelDeploymentApiUrlInvalid.value = true
    valid = false
  }
  if (!manageModelDeploymentStatus.value) {
    manageModelDeploymentStatusInvalid.value = true
    valid = false
  }
  if (manageModelDeploymentDeploymentDate.value) {
    if (!isValidDate(manageModelDeploymentDeploymentDate.value)) {
      manageModelDeploymentDeploymentDateInvalid.value = true
      valid = false
    }
  }
  if (manageModelDeploymentDiscontinuedDate.value) {
    if (!isValidDate(manageModelDeploymentDiscontinuedDate.value)) {
      manageModelDeploymentDiscontinuedDateInvalid.value = true
      valid = false
    }
  }
  if (!manageModelDeploymentModel.value) {
    manageModelDeploymentModelInvalid.value = true
    valid = false
  }

  if (!valid) {
    return false
  }

  const md = manageModelDeploymentMetadata.value ?? {}
  md.restApi = manageModelDeploymentApiUrl.value

  if (manageModelDeploymentId.value) {
    const payload = {
      name: manageModelDeploymentName.value,
      machine_name: manageModelDeploymentMachineName.value,
      status: manageModelDeploymentStatus.value,
      deployment_date: convertDateFormat(manageModelDeploymentDeploymentDate.value),
      discontinued_date: convertDateFormat(manageModelDeploymentDiscontinuedDate.value),
      llm_models_id: manageModelDeploymentModel.value,
      ai_model_deployment_credentials_id: manageModelDeploymentCredential.value,
      metadata: JSON.stringify(md)
    }
    manageModelDeploymentSubmitting.value = true
    try {
      const result = await patch('/crbn-tech/llm-model-deployments/' + manageModelDeploymentId.value, payload)
      if (result.id) {
        for (let i = 0; i < modelDeployments.value.length; i++) {
          if (String(modelDeployments.value[i].id) === String(result.id)) {
            modelDeployments.value[i] = decodeModelDeployment(result, models)
          }
        }
      }
      else {
        handleError();
      }
      handleClose()
    }
    catch (e) {
      manageModelDeploymentSubmitting.value = false
      handleError(e)
    }
  }
  else {
    const payload = {
      name: manageModelDeploymentName.value,
      machine_name: manageModelDeploymentMachineName.value,
      status: manageModelDeploymentStatus.value,
      deployment_date: convertDateFormat(manageModelDeploymentDeploymentDate.value),
      discontinued_date: convertDateFormat(manageModelDeploymentDiscontinuedDate.value),
      llm_models_id: manageModelDeploymentModel.value,
      cephisso_platform_accounts_id: selectedAccount.value,
      ai_model_deployment_credentials_id: manageModelDeploymentCredential.value,
      metadata: JSON.stringify(md)
    }
    manageModelDeploymentSubmitting.value = true
    try {
      const result = await post('/crbn-tech/llm-model-deployments', payload)
      if (result.id) {
        modelDeployments.value.push(decodeModelDeployment(result, models))
      }
      else {
        handleError()
      }
      handleClose()
    }
    catch (e) {
      manageModelDeploymentSubmitting.value = false
      handleError(e)
    }
  }
}

</script>

<style scoped>
</style>